import { AntifraudAnalysis } from '@/graphql/__generated__/onboarding';
import {
	AntifraudAnalysis as LegalAntifraudAnalysis,
	AntifraudAnalysisV1 as LegalAntifraudAnalysisV1,
	AntifraudAnalysisType
} from '@/graphql/__generated__/onboarding-legal';

const TRULLY_ANTIFRAUD_TYPE = AntifraudAnalysisType.V1;
const UNICO_ANTIFRAUD_TYPE = AntifraudAnalysisType.V2;

type UnicoAntifraudAnalysis = {
	label?: string | null;
	reason?: string | null;
	request_id?: string;
	document?: {
		details?: {
			document_id?: number;
			detected?: boolean;
			forensics?: {
				is_valid?: string;
			};
		};
		back?: {
			cic?: string;
			citizen_id?: string;
			mrz?: string;
		};
		front?: {
			information?: {
				name?: { text?: string; valid?: boolean };
				address?: { text?: string; valid?: boolean };
				curp?: { text?: string; valid?: boolean };
				electoral_key?: { text?: string; valid?: boolean };
				valid_thru?: { text?: string; valid?: boolean };
				last_name?: { text?: string; valid?: boolean };
				birthdate?: { text?: string; valid?: boolean };
				complete_name?: { text?: string; valid?: boolean };
				mother_last_name?: { text?: string; valid?: boolean };
				registration_year?: { text?: string; valid?: boolean };
				sex?: { valid?: boolean };
			};
			face_analysis?: {
				match?: boolean;
				warnings?: Record<string, string>;
				last_seen?: string;
				inquiry_date?: string;
				match_fraud_flag?: boolean;
				times_seen_last_month?: number;
				seen_different_companies?: number;
				face_id?: number;
				face_id_v2?: number;
				first_seen?: string;
				last_seen_by_your_company?: string;
				seen_by_your_company?: boolean;
				times_seen_by_your_company?: number;
				unique_face_id_v2?: number;
				gender?: string;
			};
		};
	};
	selfie?: {
		match?: boolean;
		warnings?: Record<string, string>;
		last_seen?: string;
		inquiry_date?: string;
		match_fraud_flag?: boolean;
		times_seen_last_month?: number;
		seen_different_companies?: number;
		face_id?: number;
		face_id_v2?: number;
		first_seen?: string;
		last_seen_by_your_company?: string;
		seen_by_your_company?: boolean;
		times_seen_by_your_company?: number;
		unique_face_id_v2?: number;
		first_seen_image?: boolean;
	};
	curp?:
		| {
				state_of_birth?: string;
				date_of_birth?: string;
				curp?: string;
				age?: number;
				gender?: string;
				government_name?: string;
				is_mexican?: boolean;
				government_valid?: boolean;
				deceased?: boolean;
				name_to_CURP_valid?: boolean;
				state_iso?: string;
				error?: string;
		  }
		| {
				curp?: string;
				government_valid?: boolean;
				error?: string;
		  };
	face_match?: boolean;
	unico?: {
		result?: string;
		process_id?: string;
	};
};

type NormalizedAntifraudData = {
	label: string | null | undefined;
	warnings: (string | null)[] | null | undefined;
	identityFraudScore: number | null | undefined;
	unicoResult: string | null | undefined;
	isUnico: boolean;
	pep: any[] | null | undefined;
	sanctions: any[] | null | undefined;
	selfieAndOfficialIdMatch: boolean | null | undefined;
	locationAndAddressDistance:
		| {
				level: string | null | undefined;
				observation: string | null | undefined;
		  }
		| null
		| undefined;
	address:
		| {
				validatedFullAddress: string | null | undefined;
				isNearToPrison: boolean | null | undefined;
				isOutsideOfTerritory: boolean | null | undefined;
				isInsideOfPrison: boolean | null | undefined;
				isInHighRiskLocation: boolean | null | undefined;
				error: string | null | undefined;
		  }
		| null
		| undefined;
	location:
		| {
				validatedFullAddress: string | null | undefined;
				isNearToPrison: boolean | null | undefined;
				isOutsideOfTerritory: boolean | null | undefined;
				isInsideOfPrison: boolean | null | undefined;
				isInHighRiskLocation: boolean | null | undefined;
				error: string | null | undefined;
		  }
		| null
		| undefined;
	rfc:
		| {
				blacklistStatus: string | null | undefined;
				isValid: boolean | null | undefined;
				observation: string | null | undefined;
				validatedRfc: string | null | undefined;
		  }
		| null
		| undefined;
	officialIdRfc:
		| {
				blacklistStatus: string | null | undefined;
				isValid: boolean | null | undefined;
				observation: string | null | undefined;
				validatedRfc: string | null | undefined;
		  }
		| null
		| undefined;
	curp:
		| {
				curp: string | null | undefined;
				governmentName: string | null | undefined;
				isValid: boolean | null | undefined;
				dateOfBirth: string | null | undefined;
				gender: string | null | undefined;
				isMexican: boolean | null | undefined;
				error: string | null | undefined;
		  }
		| null
		| undefined;
	officialIdCurp:
		| {
				curp: string | null | undefined;
				governmentName: string | null | undefined;
				isValid: boolean | null | undefined;
				dateOfBirth: string | null | undefined;
				gender: string | null | undefined;
				isMexican: boolean | null | undefined;
				error: string | null | undefined;
		  }
		| null
		| undefined;
	ine:
		| {
				curp: string | null | undefined;
				firstLastName: string | null | undefined;
				secondLastName: string | null | undefined;
				names: string | null | undefined;
				validThru: string | null | undefined;
				electoralKey: string | null | undefined;
				cic: string | null | undefined;
				citizenId: string | null | undefined;
				mrz: string | null | undefined;
				registrationYear: string | null | undefined;
				sex: boolean | null | undefined;
		  }
		| null
		| undefined;
	passport:
		| {
				curp: string | null | undefined;
				lastNames: string | null | undefined;
				names: string | null | undefined;
				dateOfBirth: string | null | undefined;
				citizenship: string | null | undefined;
				expirationDate: string | null | undefined;
				passportNumber: string | null | undefined;
		  }
		| null
		| undefined;
	selfieFaceRecognition:
		| {
				lastSeenInTrullyNetwork: string | null | undefined;
				isMarkedAsFraudInTrullyNetwork: boolean | null | undefined;
				timesSeenLastMonthInTrullyNetwork: number | null | undefined;
				warnings: (string | null)[] | null | undefined;
				companiesSeen: number | null | undefined;
				gender: string | null | undefined;
				confidenceScore: string | null | undefined;
				lastSeenByYourCompany: string | null | undefined;
				matchFraudFlag: boolean | null | undefined;
		  }
		| null
		| undefined;
	officialIdFaceRecognition:
		| {
				lastSeenInTrullyNetwork: string | null | undefined;
				isMarkedAsFraudInTrullyNetwork: boolean | null | undefined;
				timesSeenLastMonthInTrullyNetwork: number | null | undefined;
				warnings: (string | null)[] | null | undefined;
				companiesSeen: number | null | undefined;
				gender: string | null | undefined;
				confidenceScore: string | null | undefined;
				lastSeenByYourCompany: string | null | undefined;
				matchFraudFlag: boolean | null | undefined;
		  }
		| null
		| undefined;
	ipAddress:
		| {
				isBlacklisted: boolean | null | undefined;
				blacklistedCount: number | null | undefined;
				isTorConnection: boolean | null | undefined;
				isVpnConnection: boolean | null | undefined;
		  }
		| null
		| undefined;
	email:
		| {
				deliverability: string | null | undefined;
				sanitizedEmail: string | null | undefined;
				isValid: boolean | null | undefined;
				isDisposable: boolean | null | undefined;
				isLeaked: boolean | null | undefined;
				isSpamHoneypot: boolean | null | undefined;
				hasRecentAbuseInTrullyNetwork: boolean | null | undefined;
				isFrequentComplainer: boolean | null | undefined;
				spamTrapScore: string | null | undefined;
				fraudScore: number | null | undefined;
		  }
		| null
		| undefined;
	phoneNumber:
		| {
				isValid: boolean | null | undefined;
				isActive: boolean | null | undefined;
				isDigitalPhoneNumber: boolean | null | undefined;
				isSpammer: boolean | null | undefined;
				isLeaked: boolean | null | undefined;
				hasRecentAbuseInTrullyNetwork: boolean | null | undefined;
				hasFraudulentBehavior: boolean | null | undefined;
				carrier: string | null | undefined;
				country: string | null | undefined;
				city: string | null | undefined;
				fraudScore: number | null | undefined;
				onWhatsapp: boolean | null | undefined;
		  }
		| null
		| undefined;
};

const extractUnicoWarnings = (rawData: UnicoAntifraudAnalysis): (string | null)[] | undefined => {
	const warnings: (string | null)[] = [];

	if (rawData?.document?.front?.face_analysis?.warnings) {
		const docWarnings = rawData.document.front.face_analysis.warnings;
		Object.entries(docWarnings).forEach(([key, value]) => {
			if (value) warnings.push(`${key}: ${value}`);
		});
	}

	if (rawData?.selfie?.warnings) {
		const selfieWarnings = rawData.selfie.warnings;
		Object.entries(selfieWarnings).forEach(([key, value]) => {
			if (value) warnings.push(`Selfie ${key}: ${value}`);
		});
	}

	return warnings.length > 0 ? warnings : undefined;
};

const extractCurpError = (curpData: UnicoAntifraudAnalysis['curp']): string | null => {
	if (!curpData) return null;

	if (curpData.error) {
		if (curpData.error === 'The CURP does not exist in Renapo') {
			return 'Not exist in renapo';
		}
		return curpData.error;
	}

	if ('deceased' in curpData && curpData.deceased) {
		return 'Deceased';
	}

	if ('name_to_CURP_valid' in curpData && curpData.name_to_CURP_valid === false) {
		return 'Name to CURP validation failed';
	}

	if (curpData.government_valid === false) {
		return 'Not exist in renapo';
	}

	return null;
};

const normalizeUnicoResultEnum = (result: string | null | undefined): string | null => {
	if (!result) return null;
	const upperResult = result.toUpperCase();
	const enumToDisplayMap: Record<string, string> = {
		PROCESS_RESULT_ERROR: 'ERROR',
		PROCESS_RESULT_EN_VIVO: 'EN VIVO',
		PROCESS_RESULT_LIVE: 'EN VIVO',
		PROCESS_RESULT_NOT_EN_VIVO: 'NO EN VIVO',
		PROCESS_RESULT_HIGH_RISK: 'ALTO RIESGO',
		PROCESS_RESULT_CRITICAL_RISK: 'RIESGO CRITICO',
		PROCESS_RESULT_NOT_APPROVED: 'ALTO RIESGO'
	};
	return enumToDisplayMap[upperResult] || 'ERROR';
};

const normalizeUnicoAntifraudAnalysis = (
	rawData: UnicoAntifraudAnalysis
): NormalizedAntifraudData => {
	const document = rawData?.document;
	const documentFront = document?.front;
	const documentBack = document?.back;
	const documentInfo = documentFront?.information;
	const documentAddress = documentInfo?.address;
	const faceAnalysis = documentFront?.face_analysis;
	const selfie = rawData?.selfie;
	const curpData = rawData?.curp;

	const curpError = extractCurpError(curpData);
	const curpValue = curpData?.curp || null;
	const governmentName =
		curpData && 'government_name' in curpData ? curpData.government_name : null;
	const curpDataAny = curpData as any;
	const isValid =
		curpDataAny && typeof curpDataAny.government_valid === 'boolean'
			? curpDataAny.government_valid
			: null;
	const dateOfBirth = curpData && 'date_of_birth' in curpData ? curpData.date_of_birth : null;
	const gender = curpData && 'gender' in curpData ? curpData.gender : null;
	const isMexican = curpData && 'is_mexican' in curpData ? curpData.is_mexican : null;

	return {
		label: rawData?.label || null,
		warnings: extractUnicoWarnings(rawData) || undefined,
		identityFraudScore: null,
		unicoResult: normalizeUnicoResultEnum(rawData?.unico?.result) || null,
		isUnico: true,
		pep: null,
		sanctions: null,
		selfieAndOfficialIdMatch: rawData?.face_match || null,
		locationAndAddressDistance: null,
		address: documentAddress
			? {
					validatedFullAddress: documentAddress.text || null,
					isNearToPrison: null,
					isOutsideOfTerritory: null,
					isInsideOfPrison: null,
					isInHighRiskLocation: null,
					error: documentAddress.valid === false ? 'Invalid address' : null
				}
			: null,
		location: null,
		rfc: null,
		officialIdRfc: null,
		curp: curpData
			? {
					curp: curpValue,
					governmentName: governmentName,
					isValid: isValid,
					dateOfBirth: dateOfBirth,
					gender: gender,
					isMexican: isMexican,
					error: curpError
				}
			: null,
		officialIdCurp: curpData
			? {
					curp: curpValue,
					governmentName: governmentName,
					isValid: isValid,
					dateOfBirth: dateOfBirth,
					gender: gender,
					isMexican: isMexican,
					error: curpError
				}
			: null,
		ine:
			documentInfo || documentBack
				? {
						curp: curpData?.curp || null,
						firstLastName: documentInfo?.last_name?.text || null,
						secondLastName: documentInfo?.mother_last_name?.text || null,
						names: documentInfo?.name?.text || null,
						validThru: documentInfo?.valid_thru?.text || null,
						electoralKey: documentInfo?.electoral_key?.text || null,
						cic: documentBack?.cic || null,
						citizenId: documentBack?.citizen_id || null,
						mrz: documentBack?.mrz || null,
						registrationYear: documentInfo?.registration_year?.text || null,
						sex: documentInfo?.sex?.valid !== undefined ? documentInfo.sex.valid : null
					}
				: null,
		passport: null,
		selfieFaceRecognition: selfie
			? {
					lastSeenInTrullyNetwork: selfie.last_seen || null,
					isMarkedAsFraudInTrullyNetwork: selfie.match_fraud_flag || false,
					timesSeenLastMonthInTrullyNetwork: selfie.times_seen_last_month || null,
					warnings: selfie.warnings
						? (Object.entries(selfie.warnings)
								.map(([key, value]) => (value ? `${key}: ${value}` : null))
								.filter(Boolean) as string[])
						: null,
					companiesSeen: selfie.seen_different_companies || null,
					gender: faceAnalysis?.gender || null,
					confidenceScore: null,
					lastSeenByYourCompany: selfie.last_seen_by_your_company || null,
					matchFraudFlag: selfie.match_fraud_flag || false
				}
			: null,
		officialIdFaceRecognition: faceAnalysis
			? {
					lastSeenInTrullyNetwork: faceAnalysis.last_seen || null,
					isMarkedAsFraudInTrullyNetwork: faceAnalysis.match_fraud_flag || false,
					timesSeenLastMonthInTrullyNetwork: faceAnalysis.times_seen_last_month || null,
					warnings: faceAnalysis.warnings
						? (Object.entries(faceAnalysis.warnings)
								.map(([key, value]) => (value ? `${key}: ${value}` : null))
								.filter(Boolean) as string[])
						: null,
					companiesSeen: faceAnalysis.seen_different_companies || null,
					gender: faceAnalysis.gender || null,
					confidenceScore: null,
					lastSeenByYourCompany: faceAnalysis.last_seen_by_your_company || null,
					matchFraudFlag: faceAnalysis.match_fraud_flag || false
				}
			: null,
		ipAddress: null,
		email: null,
		phoneNumber: null
	};
};

const normalizeTrullyAntifraudAnalysis = (
	trullyData: AntifraudAnalysis | LegalAntifraudAnalysisV1 | null | undefined
): NormalizedAntifraudData => {
	if (!trullyData || (typeof trullyData === 'object' && Object.keys(trullyData).length === 0)) {
		return {
			label: null,
			warnings: null,
			identityFraudScore: null,
			unicoResult: null,
			isUnico: false,
			pep: null,
			sanctions: null,
			selfieAndOfficialIdMatch: null,
			locationAndAddressDistance: null,
			address: null,
			location: null,
			rfc: null,
			officialIdRfc: null,
			curp: null,
			officialIdCurp: null,
			ine: null,
			passport: null,
			selfieFaceRecognition: null,
			officialIdFaceRecognition: null,
			ipAddress: null,
			email: null,
			phoneNumber: null
		};
	}

	return {
		label: trullyData.label,
		warnings: trullyData.warnings,
		identityFraudScore: trullyData.identityFraudScore,
		unicoResult: null,
		isUnico: false,
		pep: trullyData.pep,
		sanctions: trullyData.sanctions,
		selfieAndOfficialIdMatch: trullyData.selfieAndOfficialIdMatch,
		locationAndAddressDistance: trullyData.locationAndAddressDistance
			? {
					level: trullyData.locationAndAddressDistance.level,
					observation: trullyData.locationAndAddressDistance.observation
				}
			: null,
		address: trullyData.address
			? {
					validatedFullAddress: trullyData.address.validatedFullAddress,
					isNearToPrison: trullyData.address.isNearToPrison,
					isOutsideOfTerritory: trullyData.address.isOutsideOfTerritory,
					isInsideOfPrison: trullyData.address.isInsideOfPrison,
					isInHighRiskLocation: trullyData.address.isInHighRiskLocation,
					error: trullyData.address.error
				}
			: null,
		location: trullyData.location
			? {
					validatedFullAddress: trullyData.location.validatedFullAddress,
					isNearToPrison: trullyData.location.isNearToPrison,
					isOutsideOfTerritory: trullyData.location.isOutsideOfTerritory,
					isInsideOfPrison: trullyData.location.isInsideOfPrison,
					isInHighRiskLocation: trullyData.location.isInHighRiskLocation,
					error: trullyData.location.error
				}
			: null,
		rfc: trullyData.rfc
			? {
					blacklistStatus: trullyData.rfc.blacklistStatus,
					isValid: trullyData.rfc.isValid,
					observation: trullyData.rfc.observation,
					validatedRfc: trullyData.rfc.validatedRfc
				}
			: null,
		officialIdRfc: trullyData.officialIdRfc
			? {
					blacklistStatus: trullyData.officialIdRfc.blacklistStatus,
					isValid: trullyData.officialIdRfc.isValid,
					observation: trullyData.officialIdRfc.observation,
					validatedRfc: trullyData.officialIdRfc.validatedRfc
				}
			: null,
		curp: trullyData.curp
			? {
					curp: trullyData.curp.curp,
					governmentName: trullyData.curp.governmentName,
					isValid: trullyData.curp.isValid,
					dateOfBirth: trullyData.curp.dateOfBirth,
					gender: trullyData.curp.gender,
					isMexican: trullyData.curp.isMexican,
					error: trullyData.curp.error
				}
			: null,
		officialIdCurp: trullyData.officialIdCurp
			? {
					curp: trullyData.officialIdCurp.curp,
					governmentName: trullyData.officialIdCurp.governmentName,
					isValid: trullyData.officialIdCurp.isValid,
					dateOfBirth: trullyData.officialIdCurp.dateOfBirth,
					gender: trullyData.officialIdCurp.gender,
					isMexican: trullyData.officialIdCurp.isMexican,
					error: trullyData.officialIdCurp.error
				}
			: null,
		ine: trullyData.ine
			? {
					curp: trullyData.ine.curp,
					firstLastName: trullyData.ine.firstLastName,
					secondLastName: trullyData.ine.secondLastName,
					names: trullyData.ine.names,
					validThru: trullyData.ine.validThru,
					electoralKey: trullyData.ine.electoralKey,
					cic: null,
					citizenId: null,
					mrz: null,
					registrationYear: null,
					sex: null
				}
			: null,
		passport: trullyData.passport
			? {
					curp: trullyData.passport.curp,
					lastNames: trullyData.passport.lastNames,
					names: trullyData.passport.names,
					dateOfBirth: trullyData.passport.dateOfBirth,
					citizenship: trullyData.passport.citizenship,
					expirationDate: trullyData.passport.expirationDate,
					passportNumber: trullyData.passport.passportNumber
				}
			: null,
		selfieFaceRecognition: trullyData.selfieFaceRecognition
			? {
					lastSeenInTrullyNetwork: trullyData.selfieFaceRecognition.lastSeenInTrullyNetwork,
					isMarkedAsFraudInTrullyNetwork:
						trullyData.selfieFaceRecognition.isMarkedAsFraudInTrullyNetwork,
					timesSeenLastMonthInTrullyNetwork:
						trullyData.selfieFaceRecognition.timesSeenLastMonthInTrullyNetwork,
					warnings: trullyData.selfieFaceRecognition.warnings,
					companiesSeen: trullyData.selfieFaceRecognition.companiesSeen,
					gender: trullyData.selfieFaceRecognition.gender,
					confidenceScore: trullyData.selfieFaceRecognition.confidenceScore,
					lastSeenByYourCompany: null,
					matchFraudFlag: null
				}
			: null,
		officialIdFaceRecognition: trullyData.officialIdFaceRecognition
			? {
					lastSeenInTrullyNetwork: trullyData.officialIdFaceRecognition.lastSeenInTrullyNetwork,
					isMarkedAsFraudInTrullyNetwork:
						trullyData.officialIdFaceRecognition.isMarkedAsFraudInTrullyNetwork,
					timesSeenLastMonthInTrullyNetwork:
						trullyData.officialIdFaceRecognition.timesSeenLastMonthInTrullyNetwork,
					warnings: trullyData.officialIdFaceRecognition.warnings,
					companiesSeen: trullyData.officialIdFaceRecognition.companiesSeen,
					gender: trullyData.officialIdFaceRecognition.gender,
					confidenceScore: trullyData.officialIdFaceRecognition.confidenceScore,
					lastSeenByYourCompany: null,
					matchFraudFlag: null
				}
			: null,
		ipAddress: trullyData.ipAddress
			? {
					isBlacklisted: trullyData.ipAddress.isBlacklisted,
					blacklistedCount: trullyData.ipAddress.blacklistedCount,
					isTorConnection: trullyData.ipAddress.isTorConnection,
					isVpnConnection: trullyData.ipAddress.isVpnConnection
				}
			: null,
		email: trullyData.email
			? {
					deliverability: trullyData.email.deliverability,
					sanitizedEmail: trullyData.email.sanitizedEmail,
					isValid: trullyData.email.isValid,
					isDisposable: trullyData.email.isDisposable,
					isLeaked: trullyData.email.isLeaked,
					isSpamHoneypot: trullyData.email.isSpamHoneypot,
					hasRecentAbuseInTrullyNetwork: trullyData.email.hasRecentAbuseInTrullyNetwork,
					isFrequentComplainer: trullyData.email.isFrequentComplainer,
					spamTrapScore: trullyData.email.spamTrapScore,
					fraudScore: trullyData.email.fraudScore
				}
			: null,
		phoneNumber: trullyData.phoneNumber
			? {
					isValid: trullyData.phoneNumber.isValid,
					isActive: trullyData.phoneNumber.isActive,
					isDigitalPhoneNumber: trullyData.phoneNumber.isDigitalPhoneNumber,
					isSpammer: trullyData.phoneNumber.isSpammer,
					isLeaked: trullyData.phoneNumber.isLeaked,
					hasRecentAbuseInTrullyNetwork: trullyData.phoneNumber.hasRecentAbuseInTrullyNetwork,
					hasFraudulentBehavior: trullyData.phoneNumber.hasFraudulentBehavior,
					carrier: trullyData.phoneNumber.carrier,
					country: trullyData.phoneNumber.country,
					city: trullyData.phoneNumber.city,
					fraudScore: trullyData.phoneNumber.fraudScore,
					onWhatsapp: trullyData.phoneNumber.onWhatsapp
				}
			: null
	};
};

const isUnicoAntifraudAnalysis = (data: any): data is UnicoAntifraudAnalysis => {
	if (!data || typeof data !== 'object') return false;

	if (data.label && Array.isArray(data.warnings) && typeof data.identityFraudScore === 'number') {
		return false;
	}

	return (
		data.document !== undefined ||
		data.selfie !== undefined ||
		(data.curp &&
			typeof data.curp === 'object' &&
			('state_of_birth' in data.curp ||
				('government_valid' in data.curp && !('isValid' in data.curp)) ||
				('error' in data.curp && !('governmentName' in data.curp)))) ||
		data.face_match !== undefined ||
		data.unico !== undefined ||
		data.request_id !== undefined
	);
};

export const normalizeAntifraudData = (
	antifraudAnalysis:
		| AntifraudAnalysis
		| LegalAntifraudAnalysis
		| UnicoAntifraudAnalysis
		| any
		| null
		| undefined
): NormalizedAntifraudData => {
	if (!antifraudAnalysis) {
		return normalizeTrullyAntifraudAnalysis(null);
	}

	if (isUnicoAntifraudAnalysis(antifraudAnalysis)) {
		return normalizeUnicoAntifraudAnalysis(antifraudAnalysis);
	}

	const legalAntifraud = antifraudAnalysis as LegalAntifraudAnalysis;
	if ('type' in legalAntifraud) {
		if (legalAntifraud.type === UNICO_ANTIFRAUD_TYPE && legalAntifraud.v2Analysis?.rawData) {
			return normalizeUnicoAntifraudAnalysis(legalAntifraud.v2Analysis.rawData);
		}
		if (legalAntifraud.type === TRULLY_ANTIFRAUD_TYPE && legalAntifraud.v1Analysis) {
			return normalizeTrullyAntifraudAnalysis(legalAntifraud.v1Analysis);
		}
	}

	const onboardingAntifraud = antifraudAnalysis as AntifraudAnalysis;
	return normalizeTrullyAntifraudAnalysis(onboardingAntifraud);
};

export type { NormalizedAntifraudData };
